<script setup lang="ts">
import { computed, useTemplateRef } from 'vue'
import type { HTMLAttributes } from 'vue'
import { useAutoCollapse } from './useAutoCollapse'

defineOptions({
    compatConfig: {
        MODE: 3,
    },
})

const props = withDefaults(defineProps<{
    class?: HTMLAttributes['class']
    contentClass?: HTMLAttributes['class']
}>(), {
    class: '',
    contentClass: '',
})

const emit = defineEmits<{
    opened: []
    closed: []
}>()

const modelValue = defineModel<boolean>({ default: false })
const outerEl = useTemplateRef<HTMLElement>('outer')
const innerEl = useTemplateRef<HTMLElement>('inner')

const { onTransitionEnd: handleTransitionEnd, isContentCollapsed } = useAutoCollapse(
    outerEl,
    innerEl,
    computed(() => modelValue.value ?? false),
)

function onTransitionEnd(e: TransitionEvent) {
    handleTransitionEnd(e)
    if (e.propertyName !== 'max-height') return
    if (modelValue.value && !isContentCollapsed.value) {
        emit('opened')
    } else if (!modelValue.value) {
        emit('closed')
    }
}
</script>

<template>
    <div
        ref="outer"
        class="auto-collapse"
        :class="[props.class, { open: modelValue }]"
        @transitionend="onTransitionEnd"
    >
        <div
            ref="inner"
            :class="props.contentClass"
        >
            <slot></slot>
        </div>
    </div>
</template>

<style scoped>
.auto-collapse {
    overflow: hidden;
    opacity: 0;
    transition: opacity .3s, max-height .3s var(--ease-out-quart);

    &.open {
        opacity: 1;
        overflow: visible;
    }
}
</style>
